import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const PAGES_VIEWED_KEY = 'barguide-pages-viewed';
const DISMISSED_KEY = 'barguide-banner-dismissed';
const CLICKED_KEY = 'barguide-banner-clicked';

const DISMISS_COOLDOWN_DAYS = 14;
const CLICK_COOLDOWN_DAYS = 30;
const MIN_PAGE_VIEWS = 3;

function isWithinCooldown(key: string, days: number): boolean {
  const timestamp = localStorage.getItem(key);
  if (!timestamp) return false;
  const elapsed = Date.now() - new Date(timestamp).getTime();
  return elapsed < days * 24 * 60 * 60 * 1000;
}

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  const isMobileUA = /Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini/i.test(ua);
  const isSmallViewport = window.innerWidth < 1024;
  return isMobileUA && isSmallViewport;
}

function isSafari(): boolean {
  const ua = navigator.userAgent;
  // Chrome, Firefox, Edge on iOS all include their name AND Safari in the UA.
  // Only real Safari has Safari without Chrome/CriOS/Firefox/FxiOS/Edg.
  if (/CriOS|Chrome|FxiOS|Firefox|Edg/i.test(ua)) return false;
  return /Safari/i.test(ua);
}

function isStandalone(): boolean {
  if ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone) return true;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  return false;
}

function isWebView(): boolean {
  const ua = navigator.userAgent;
  return /FBAN|FBAV|Instagram|; wv\)/i.test(ua);
}

export function useAppInstallBanner() {
  const location = useLocation();
  const [pageViews, setPageViews] = useState(() => {
    const stored = sessionStorage.getItem(PAGES_VIEWED_KEY);
    return stored ? parseInt(stored, 10) : 0;
  });
  const [dismissed, setDismissed] = useState(false);
  const prevPathname = useRef(location.pathname);

  // Track page views on pathname change
  useEffect(() => {
    if (location.pathname === prevPathname.current) return;
    prevPathname.current = location.pathname;

    const next = pageViews + 1;
    setPageViews(next);
    sessionStorage.setItem(PAGES_VIEWED_KEY, String(next));
  }, [location.pathname, pageViews]);

  const shouldShow =
    !dismissed &&
    isMobileDevice() &&
    !isSafari() &&
    !isStandalone() &&
    !isWebView() &&
    !isWithinCooldown(DISMISSED_KEY, DISMISS_COOLDOWN_DAYS) &&
    !isWithinCooldown(CLICKED_KEY, CLICK_COOLDOWN_DAYS) &&
    pageViews >= MIN_PAGE_VIEWS;

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    setDismissed(true);
  }, []);

  const onInstallClick = useCallback(() => {
    localStorage.setItem(CLICKED_KEY, new Date().toISOString());
  }, []);

  return { shouldShow, dismiss, onInstallClick };
}
